import request from 'sb-request/graphql'
import type { ReducerState } from 'redux/models/cart/reducer'

import cartQuery from './cart.graphql'
import cartModifyQuery from './cartModify.graphql'
import cartDeleteQuery from './cartDelete.graphql'
import cartClearQuery from './cartClear.graphql'

import type { CartPayload } from './cartGQL'
import type { CartModifyVariables, CartModifyPayload } from './cartModifyGQL'
import type { CartDeleteVariables, CartDeletePayload } from './cartDeleteGQL'
import type { CartClearVariables, CartClearPayload } from './cartClearGQL'


export type ModifiedCartData = Omit<ReducerState, 'isFetching' | 'isVisible'>

const modifyCartData = (cart: CartPayload['currentUser']['data']['cart']): ModifiedCartData => {
  const { subscription, products, shipping, total } = cart

  const modifiedSubscription = !subscription ? null : {
    name: subscription.label,
    quantity: 1,
    size: 0.27, // TODO ask backend for this field - added on 5/12/20 by pavelivanov
    volume: 0.27, // TODO ask backend for this field - added on 5/12/20 by pavelivanov
    volumeUnit: 'oz', // TODO ask backend for this field - added on 5/12/20 by pavelivanov
    volumeNotes: subscription.note,
    price: subscription.price,
  }

  const modifiedProducts = !products ? {
    ids: [],
    items: {},
  } : {
    ids: products.map(({ id }) => Number(id)),
    items: products.reduce((result, { id: productLineItemId, product, quantity, price: totalPrice }) => {
      const { price, unit, ...restProduct } = product

      return {
        ...result,
        [productLineItemId]: {
          tradingId: productLineItemId, // TODO replace "tradingId" with "productLineItemId" after move to Recurly - added on 6/9/20 by pavelivanov
          ...restProduct,
          volumeUnit: unit,
          quantity,
          price,
          totalPrice,
        },
      }
    }, {}),
  }

  let productsCount = 0

  if (products && products.length) {
    productsCount = products.reduce((count: number, { quantity }) => count + Number(quantity), 0)
  }

  const totalCount = productsCount + (subscription ? 1 : 0)

  return {
    items: {
      subscription: modifiedSubscription,
      products: modifiedProducts,
    },
    discount: null,
    shipping: shipping ? shipping[0].price : null,
    areProductsExist: Boolean(products && products.length),
    isSubscriptionExist: Boolean(subscription),
    isFlashSaleExist: false, // TODO ask backend about this tag in new GQL - added on 5/12/20 by pavelivanov
    isFlashSaleRemoved: false, // TODO ask backend about this tag in new GQL - added on 5/12/20 by pavelivanov
    totalCount,
    totalPrice: total,
    subtotal: total,
    appliedPriceRules: undefined, // TODO ask backend about this tag in new GQL - added on 5/12/20 by pavelivanov
    recommendationTag: undefined, // TODO ask backend about this tag in new GQL - added on 5/12/20 by pavelivanov
  }
}

export type GetCartResult = {
  data: ModifiedCartData
  error: { code: string, message: string }
}

const getCart = (): Promise<GetCartResult> =>
  request<never, CartPayload, GetCartResult>('getCart', {
    query: cartQuery,
  }, {
    modifyResult: ({ currentUser: { data, error } }) => ({
      data: data && modifyCartData(data.cart),
      error,
    }),
  })


export type ModifyCartItemsResult = {
  data: ModifiedCartData
  error: { code: string, message: string }
}

const modifyCartItems = (input: CartModifyVariables['input']): Promise<ModifyCartItemsResult> =>
  request<CartModifyVariables, CartModifyPayload, ModifyCartItemsResult>('modifyCartItems', {
    query: cartModifyQuery,
    variables: { input },
  }, {
    modifyResult: ({ cartModify: { data, error } }) => ({
      data: data && modifyCartData(data.cart),
      error,
    }),
  })


export type RemoveCartItemsResult = {
  data: ModifiedCartData
  error: { code: string, message: string }
}

const removeCartItems = (input: CartDeleteVariables['input']): Promise<RemoveCartItemsResult> =>
  request<CartDeleteVariables, CartDeletePayload, RemoveCartItemsResult>('removeCartItems', {
    query: cartDeleteQuery,
    variables: { input },
  }, {
    modifyResult: ({ cartDelete: { data, error } }) => ({
      data: data && modifyCartData(data.cart),
      error,
    }),
  })


export type ClearCartResult = {
  data: ModifiedCartData
  error: { code: string, message: string }
}

const clearCart = (input?: CartClearVariables['input']): Promise<ClearCartResult> =>
  request<CartClearVariables, CartClearPayload, ClearCartResult>('clearCart', {
    query: cartClearQuery,
    variables: { input },
  }, {
    modifyResult: ({ cartClear: { data, error } }) => ({
      data: data && modifyCartData(data.cart),
      error,
    }),
  })


export {
  getCart,
  modifyCartItems,
  removeCartItems,
  clearCart,
}
